// @givmo/compliance — running one of Compliance's gated acts through the ONE "Confirm it's you"
// (`@givmo/step-up`; CCM-R1-SIGN-WEB).
//
// The organization's sign-in check (D-SIGN-1) gates signing, withdrawing a signature and lowering
// the check itself. At a stricter level the backend refuses an
// act with the typed `step_up_required` (`context.action` names which), and the gate answers it:
// the host's re-authentication, a forced token refresh, then the act once more. Compliance's words
// for the prompt are its own rows (`COMPLIANCE_STEP_UP_COPY`), keyed by that action, so no map is
// needed; `@givmo/step-up` holds no Compliance word.
//
// The prompt names the level in force ("…within the last 30 minutes…"), so the level is read FRESH
// as the act starts, never from a read the page made minutes ago. The act itself is a thunk over
// what the person already typed, so a retry re-sends exactly that.
//
// `inFlight` is true while a run is pending and not waiting on the person at the sheet (the gate's
// `prompting`, CCM-R1-STEP-UP-C): through the level read, the act, the host's account read and the
// token refresh before the retry, so the act never sits idle and pressable while it is still
// working; at rest behind the sheet (frame 39 shows it so).

import { useCallback, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useStepUpGate, type StepUpContext } from '@givmo/step-up';
import { useCompliance } from './compliance-context';
import { signingSettingQueryKey } from './compliance-queries';
import { decodeSigningSetting } from './compliance-signing-decode';
import {
  COMPLIANCE_STEP_UP_COPY,
  COMPLIANCE_STEP_UP_TEST_ID_PREFIX,
} from './compliance-signing-model';

export interface ComplianceActGate {
  /**
   * Run one act. On a sign-in check refusal the prompt opens; a dismissal rejects with
   * `StepUpCancelledError` (`isStepUpCancelled`), and any other failure is the act's own.
   */
  run: <T>(act: () => Promise<T>) => Promise<T>;
  /** Render this beside the act. It is `null` unless a prompt is open. */
  dialog: ReactNode;
  /** Whether a run is pending now, except while it waits on the person at the sheet. */
  inFlight: boolean;
}

export function useComplianceActGate(): ComplianceActGate {
  const { orgRef, operations, organizationName } = useCompliance();
  const queryClient = useQueryClient();
  const gate = useStepUpGate({
    copy: COMPLIANCE_STEP_UP_COPY,
    testIdPrefix: COMPLIANCE_STEP_UP_TEST_ID_PREFIX,
  });
  const gateRun = gate.run;
  // How many of this page's runs are pending, from the level read to the act's last answer.
  const [running, setRunning] = useState(0);
  const run = useCallback(
    async <T,>(act: () => Promise<T>): Promise<T> => {
      setRunning((count) => count + 1);
      try {
        let level: string | undefined;
        try {
          const body = await queryClient.fetchQuery({
            queryKey: signingSettingQueryKey(orgRef),
            queryFn: () => operations.getSigningSetting(orgRef ?? ''),
            staleTime: 0,
          });
          level = decodeSigningSetting(body)?.level;
        } catch {
          // The level is only the prompt's wording; the act still runs and the server decides.
          level = undefined;
        }
        const context: StepUpContext = {
          ...(organizationName !== undefined && organizationName.trim().length > 0
            ? { organizationName: organizationName.trim() }
            : {}),
          ...(level !== undefined ? { level } : {}),
        };
        return await gateRun(act, { context });
      } finally {
        setRunning((count) => count - 1);
      }
    },
    [gateRun, orgRef, operations, organizationName, queryClient],
  );
  return { run, dialog: gate.dialog, inFlight: running > 0 && !gate.prompting };
}
